import { FC, useMemo } from 'react'
import { Select, Spin } from 'antd'
import type { SelectProps } from 'antd'
import { LoadingOutlined, CloseOutlined } from '@ant-design/icons'
import { getCourse } from '../../utils/getCourse'
import EnrollDateCheckbox from '../../../../EnrollDateCheckbox'
import spclasses from '../../../../../SetCourseClassDirectionModal/purchase.module.css'
import classes from './index.module.css'
import classNames from 'classnames'
import PaymentPeriodCheckbox from '../../../PaymentPeriodCheckbox/PaymentPeriodCheckbox'
import { ICourseEnrollmentItem } from './types'
import {
  isCourseLinkGroupSelectableTariff,
  isLessonPaymentTariff,
  isStreamOneTimeTariff,
  PAYMENT_LINK_TARIFF_TYPES,
  getCrmLinkTariffTypeLabel,
} from '../../../../tariffTypes'
import { getTariffValueMin } from '@pages/CoursesCatalog/modals/CourseEnrollmentModal/components/SelectTariffValue/SelectTariffValue'
import {
  isMonthlyCourseLinkTariff,
  isStreamCustomPeriodLinkTariff,
} from '../../../../monthlyCourseLinkDiscount.utils'
import MonthlyAdditionalCourseFields from './MonthlyAdditionalCourseFields/MonthlyAdditionalCourseFields'
import { useMonthlyAdditionalCourseForm } from './MonthlyAdditionalCourseFields/useMonthlyAdditionalCourseForm'
import StreamAdditionalCourseFields from './StreamAdditionalCourseFields/StreamAdditionalCourseFields'
import { useStreamAdditionalCourseForm } from './StreamAdditionalCourseFields/useStreamAdditionalCourseForm'
import LessonAdditionalCourseFields from './LessonAdditionalCourseFields'
import StreamOneTimeAdditionalCourseFields from './StreamOneTimeAdditionalCourseFields'
import AdditionalCourseGroupSelect from './AdditionalCourseGroupSelect'
import { useLessonAdditionalCourseForm } from './useLessonAdditionalCourseForm'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'

const OCCUPIED_TARIFF_TOOLTIP =
  'Этот тариф уже выбран в этой ссылке на покупку'

type FilterOption = SelectProps['filterOption']

const filterTariffOptionsByLabel: FilterOption = (input, option) =>
  ((option?.labelString as string) ?? '')
    .toLowerCase()
    .includes(input.toLowerCase())

export interface ISelectCourseCard {
  isLoading: boolean
  courses: ICourse[]
  allCourses: ICourse[]
  order: ICourseEnrollmentItem
  promoCode?: IPromoCodeDiscount
  /** Предзаполнение скидки из настроек доп. курсов; не фиксирует поле. */
  defaultDiscount?: number | null
  /** Тарифы этого курса, уже занятые в текущей ссылке (основной + другие доп.). */
  occupiedTariffIds?: number[]
  onChange: (
    key: keyof ICourseEnrollmentItem,
    value: number | string | boolean | null | object,
  ) => void
  onDismiss: () => void
  linkEnrollDate?: string | null
}

const SelectCourseCard: FC<ISelectCourseCard> = ({
  isLoading,
  order,
  courses,
  allCourses,
  onChange,
  onDismiss,
  linkEnrollDate,
  defaultDiscount = null,
  occupiedTariffIds = [],
}) => {
  const coursesOptions = useMemo(() => {
    return courses?.map((item) => {
      return {
        value: item.id!.toString(),
        label: item?.name ?? item?.short_name ?? '',
      }
    })
  }, [courses])

  const occupiedTariffIdSet = useMemo(
    () => new Set(occupiedTariffIds),
    [occupiedTariffIds],
  )

  const tariffsOptions = useMemo(() => {
    return (
      getCourse(allCourses, order?.courseId)
        ?.directionTariffs?.filter((tariff) =>
          PAYMENT_LINK_TARIFF_TYPES.includes(tariff?.type),
        )
        ?.map((item) => {
          const labelText = item?.name ?? `Тариф ${item.id}`
          const isOccupied = occupiedTariffIdSet.has(item.id!)

          return {
            value: item.id!.toString(),
            disabled: isOccupied,
            labelString: labelText,
            label: isOccupied ? (
              <TooltipWrapper placement="top" title={OCCUPIED_TARIFF_TOOLTIP}>
                {/* pointerEvents: antd disabled option режет hover — без этого тултип не всплывёт */}
                <span className={classes.disabledOptionLabel}>{labelText}</span>
              </TooltipWrapper>
            ) : (
              labelText
            ),
          }
        }) ?? []
    )
  }, [order?.courseId, allCourses, occupiedTariffIdSet])

  const selectedTariff = useMemo(
    () =>
      getCourse(allCourses, order?.courseId)?.directionTariffs.find(
        (item) => item.id === order?.tariffId,
      ),
    [order?.courseId, order?.tariffId, allCourses],
  )

  const monthlyForm = useMonthlyAdditionalCourseForm({
    order,
    selectedTariff,
    linkEnrollDate,
    defaultDiscount,
    onChange,
  })

  const streamForm = useStreamAdditionalCourseForm({
    order,
    selectedTariff,
    defaultDiscount,
    onChange,
  })

  const lessonForm = useLessonAdditionalCourseForm({
    order,
    selectedTariff,
    defaultDiscount,
    onChange,
  })

  const onSelectCourse = (value: string | null) => {
    if (!value) {
      onChange('courseId', null)
      onChange('tariffId', null)
      onChange('value', null)
      onChange('period_unit', null)
      onChange('price', null)
      onChange('discount', null)
      onChange('discount_mode', null)
      onChange('apply_discount_each_payment', false)
      onChange('is_period_fixed', false)
      lessonForm.clearLessonFields()
      return
    }
    // Сбрасываем тариф, но сохраняем предзаполненную скидку.
    onChange('courseId', Number(value) ?? null)
    onChange('tariffId', null)
    onChange('value', null)
    onChange('period_unit', null)
    onChange('price', null)
    onChange('discount_mode', null)
    onChange('apply_discount_each_payment', false)
    onChange('is_period_fixed', false)
    lessonForm.clearLessonFields()
  }

  const onSelectTariff = (value: string | null) => {
    if (!value) {
      onChange('tariffId', null)
      onChange('value', null)
      onChange('period_unit', null)
      onChange('price', null)
      onChange('discount_mode', null)
      onChange('apply_discount_each_payment', false)
      onChange('is_period_fixed', false)
      lessonForm.clearLessonFields()
      return
    }

    const nextTariffId = Number(value)
    if (occupiedTariffIdSet.has(nextTariffId)) {
      return
    }

    const tariff =
      getCourse(allCourses, order?.courseId)?.directionTariffs?.find(
        (item) => item?.id?.toString() === value,
      ) ?? null

    onChange('tariffId', tariff!.id)

    if (isMonthlyCourseLinkTariff(tariff?.type)) {
      lessonForm.clearLessonFields()
      monthlyForm.applyTariffSelection(tariff!)
      return
    }

    if (isStreamCustomPeriodLinkTariff(tariff?.type)) {
      lessonForm.clearLessonFields()
      streamForm.applyTariffSelection(tariff!)
      return
    }

    monthlyForm.resetManualPrice()

    if (isStreamOneTimeTariff(tariff?.type)) {
      lessonForm.applyStreamOneTimeSelection(tariff!)
      return
    }

    if (isLessonPaymentTariff(tariff?.type)) {
      lessonForm.applyTariffSelection(tariff!)
      return
    }

    lessonForm.clearLessonFields()

    const nextValue =
      tariff?.type === 'free' ? null : getTariffValueMin(tariff!)

    // Бесплатный тариф — скидку не ставим; платный — предзаполнение по порядку платных.
    onChange(
      'discount',
      tariff?.type === 'free' ? null : defaultDiscount ?? null,
    )
    onChange('value', nextValue)
    onChange('period_unit', null)
    onChange('price', null)
    onChange('apply_discount_each_payment', false)
    onChange('is_period_fixed', false)
  }

  if (isLoading) {
    return (
      <div className={classes.wrapper}>
        <Spin
          className={classes.loading}
          indicator={
            <LoadingOutlined
              spin
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          }
        />
      </div>
    )
  }

  const isSelected = order?.courseId !== null && order?.tariffId !== null
  const tariffTypeLabel = getCrmLinkTariffTypeLabel(selectedTariff?.type)

  return (
    <div
      className={classNames(
        classes.wrapper,
        isSelected && classes.wrapperSelected,
      )}
    >
      <span
        role="button"
        tabIndex={0}
        className={classes.removeButton}
        onClick={onDismiss}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onDismiss()
          }
        }}
        aria-label="Удалить дополнительный курс"
        title="Удалить"
        style={{
          width: 22,
          height: 22,
          minWidth: 22,
          minHeight: 22,
          maxWidth: 22,
          maxHeight: 22,
        }}
      >
        <CloseOutlined
          className={classes.removeIcon}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
      </span>
      <div className={spclasses.inputWithLabel}>
        <div className={spclasses.inputLabel}>Курс</div>
        <Select
          showSearch
          placeholder="Выберите курс"
          className={spclasses.select}
          optionFilterProp="label"
          value={order?.courseId?.toString()}
          options={coursesOptions}
          onChange={(value) => onSelectCourse(value as string | null)}
          allowClear={false}
        />
      </div>
      {order?.courseId?.toString() && (
        <div className={spclasses.inputWithLabel}>
          <div className={spclasses.inputLabel}>Тариф</div>
          <Select
            showSearch
            placeholder="Выберите тариф"
            className={spclasses.select}
            popupClassName={classes.tariffSelectPopup}
            filterOption={filterTariffOptionsByLabel}
            value={order?.tariffId?.toString()}
            options={tariffsOptions}
            onChange={(value) => onSelectTariff(value as string | null)}
            allowClear={false}
          />
          {tariffTypeLabel ? (
            <div className={spclasses.tariffTypeHint}>
              Тип тарифа: {tariffTypeLabel}
            </div>
          ) : null}
        </div>
      )}
      {selectedTariff?.type &&
        [
          'month',
          'stream_custom_period',
          'stream_custom_days',
          'stream_custom_weeks',
        ].includes(selectedTariff.type) && (
          <EnrollDateCheckbox
            value={order?.enrollDate}
            onChange={(value) => onChange('enrollDate', value)}
          />
        )}
      {selectedTariff &&
      isCourseLinkGroupSelectableTariff(selectedTariff.type) &&
      !isLessonPaymentTariff(selectedTariff.type) ? (
        <AdditionalCourseGroupSelect
          courseId={order.courseId}
          tariffType={selectedTariff.type}
          value={order.group_id}
          onChange={(groupId) => onChange('group_id', groupId)}
        />
      ) : null}
      {monthlyForm.isActive && selectedTariff ? (
        <MonthlyAdditionalCourseFields
          isPeriodFixed={monthlyForm.isPeriodFixed}
          periodUnit={monthlyForm.periodUnit}
          periodValue={monthlyForm.periodValue}
          price={monthlyForm.displayPrice}
          tariff={selectedTariff}
          enrollDate={monthlyForm.calculationEnrollDate}
          discount={monthlyForm.discountValue}
          discountMode={monthlyForm.discountMode}
          fullBasePrice={monthlyForm.fullBasePrice}
          suggestedPrice={monthlyForm.suggestedPrice}
          priceManuallyEdited={monthlyForm.priceManuallyEdited}
          onTogglePeriodFixed={monthlyForm.onTogglePeriodFixed}
          onChangePeriodUnit={monthlyForm.onChangePeriodUnit}
          onChangeValue={monthlyForm.onChangeValue}
          onChangeUnfixedValue={monthlyForm.onChangeUnfixedValue}
          onChangeDiscount={monthlyForm.onChangeDiscount}
          onDiscountModeChange={monthlyForm.onDiscountModeChange}
          onChangePrice={monthlyForm.onChangePrice}
          onResetSuggestedPrice={monthlyForm.onResetSuggestedPrice}
        />
      ) : streamForm.isActive && selectedTariff ? (
        <StreamAdditionalCourseFields
          tariff={selectedTariff}
          isPeriodFixed={streamForm.isPeriodFixed}
          periodUnit={streamForm.periodUnit}
          periodValue={streamForm.periodValue}
          discount={streamForm.discountValue}
          discountMode={streamForm.discountMode}
          price={streamForm.displayPrice}
          streamPeriodValue={streamForm.streamPeriodValue ?? streamForm.periodValue}
          streamBasePrice={streamForm.streamBasePrice}
          onTogglePeriodFixed={streamForm.onTogglePeriodFixed}
          onChangePeriodUnit={streamForm.onChangePeriodUnit}
          onChangeValue={streamForm.onChangeValue}
          onChangeUnfixedValue={streamForm.onChangeUnfixedValue}
          onChangeDiscount={streamForm.onChangeDiscount}
          onDiscountModeChange={streamForm.onDiscountModeChange}
          onChangePrice={streamForm.onChangePrice}
        />
      ) : lessonForm.isActive && selectedTariff ? (
        <LessonAdditionalCourseFields
          order={order}
          tariff={selectedTariff}
          defaultDiscount={defaultDiscount}
          onChange={onChange}
        />
      ) : isStreamOneTimeTariff(selectedTariff?.type) ? (
        <StreamOneTimeAdditionalCourseFields
          price={order?.price}
          onChangePrice={(price) => {
            monthlyForm.resetManualPrice()
            onChange('price', price)
          }}
        />
      ) : selectedTariff && selectedTariff.type !== 'free' ? (
        <PaymentPeriodCheckbox
          type={'course'}
          courseTariff={selectedTariff}
          value={order?.value}
          onChange={(value) => {
            monthlyForm.resetManualPrice()
            onChange('value', value === undefined ? null : value)
          }}
        />
      ) : null}
    </div>
  )
}

export default SelectCourseCard
